"use client";

import { useEffect, useState } from "react";

type SecurityData = {
  csrfToken: string;
  timestampHash: string;
  isLoading: boolean;
};

// Global cache to prevent multiple API calls
const securityCache: {
  data: SecurityData | null;
  promise: Promise<SecurityData> | null;
} = {
  data: null,
  promise: null,
};

async function fetchSecurityDataOnce(): Promise<SecurityData> {
  if (securityCache.data) {
    return securityCache.data;
  }

  if (securityCache.promise) {
    return securityCache.promise;
  }

  securityCache.promise = (async () => {
    try {
      const response = await fetch("/api/csrf-token", {
        method: "GET",
        credentials: "same-origin",
        cache: "no-cache",
      });

      if (response.ok) {
        const data = await response.json();

        const securityData: SecurityData = {
          csrfToken: data.csrfToken,
          timestampHash: data.timestampHash,
          isLoading: false,
        };
        securityCache.data = securityData;
        return securityData;
      } else {
        throw new Error("Failed to fetch CSRF token");
      }
    } catch (error) {
      console.error("Error fetching CSRF token:", error);
      securityCache.promise = null; // Reset promise on error
      throw error;
    }
  })();

  return securityCache.promise;
}

/**
 * Hook to fetch CSRF token and generate security data for forms
 */
export function useSecurity(): SecurityData {
  const [securityData, setSecurityData] = useState<SecurityData>({
    csrfToken: "",
    timestampHash: "",
    isLoading: true,
  });

  useEffect(() => {
    let isMounted = true;

    fetchSecurityDataOnce()
      .then((data) => {
        if (isMounted) {
          setSecurityData(data);
        }
      })
      .catch(() => {
        if (isMounted) {
          setSecurityData((prev) => ({ ...prev, isLoading: false }));
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return securityData;
}
